// public/js/auth.js
import AuthUI from './AuthUI.js';
import TokenManager from './TokenManager.js';
import InactivityManager from './InactivityManager.js';
import { createLogger } from '@utils/logger.js';
import config from '@/config.js';

/**
 * Main authentication controller class that handles JWT client configuration,
 * login/logout, token retrieval, and updating UI login controls.
 * @class Authenticate
 */
class Authenticate {
  /**
   * @constructor
   */
  constructor() {
    if (Authenticate.instance) return Authenticate.instance;
    Authenticate.instance = this;

    this.logger = createLogger('AuthController');
    this.baseUrl = `${window.location.protocol}//${window.location.host}/`;
    this.adminAppUrl = config.adminAppUrl;
    this.user = null;
    this.userFull = null;
    this.userFirstName = null;
    this.userLastName = null;

    // Initialize token manager
    this.tokenManager = new TokenManager();

    // Initialize inactivity manager
    this.inactivityManager = new InactivityManager(this);

    // Initialize UI handler
    this.authUI = new AuthUI(this);

    // Load user data from token manager
    this.accessToken = this.tokenManager.getAccessToken();
    this.userInfo = this.tokenManager.getUserInfo();
    if (this.userInfo) {
      this.user = this.userInfo.firstName + ' ' + this.userInfo.lastName[0] + '.';
      this.userFull = this.userInfo.firstName + ' ' + this.userInfo.lastName;
      this.userFirstName = this.userInfo.firstName;
      this.userLastName = this.userInfo.lastName;
    }
  }

  /**
   * Initializes the authentication system, configures login controls, and handles callbacks.
   */
  async init() {
    await this.authUI.setupEventListeners();
    await this.updateLoginControls();
    window.authController = this;
  }

  /**
   * Redirects to the login page
   */
  async login() {
    window.location.href = '/login.html';
  }

  /**
   * Shows registration overlay
   */
  async showRegisterOverlay() {
    this.authUI.showRegisterOverlay();
  }

  /**
   * Shows forgot password overlay
   */
  async showForgotPasswordOverlay() {
    this.authUI.showForgotPasswordOverlay();
  }

  /**
   * Shows change password overlay (for authenticated users)
   */
  async showChangePasswordOverlay() {
    this.authUI.showChangePasswordOverlay();
  }

  /**
   * Logs the user out and redirects to the specified URL.
   */
  async logout() {
    try {
      // Stop inactivity monitoring
      this.inactivityManager.stop();

      // Call logout API to invalidate refresh token
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include'
      });

      // Clear token data
      this.tokenManager.clearTokens();

      // Clear memory
      this.accessToken = null;
      this.userInfo = null;
      this.user = null;
      this.userFull = null;
      this.userFirstName = null;
      this.userLastName = null;

      // Update UI
      await this.updateLoginControls();

      // Redirect to website
      window.location.href = this.adminAppUrl;
    } catch (err) {
      this.logger.error("Logout failed:", err);

      // Even if API call fails, stop monitoring and clear local data
      this.inactivityManager.stop();
      this.tokenManager.clearTokens();

      this.accessToken = null;
      this.userInfo = null;
      this.user = null;
      this.userFull = null;
      this.userFirstName = null;
      this.userLastName = null;

      // Update UI
      await this.updateLoginControls();

      // Redirect to website
      window.location.href = this.adminAppUrl;
    }
  }

  /**
   * Retrieves the access token, refreshing if necessary.
   * @returns {Promise<string>} - The access token.
   */
  async getAccessToken() {
    try {
      this.accessToken = await this.tokenManager.getValidAccessToken();
      return this.accessToken;
    } catch (error) {
      // If refresh fails, redirect to login page
      this.logger.warn('Session expired, redirecting to login');
      window.location.href = '/login.html';
      throw new Error('Authentication required');
    }
  }

  /**
   * Updates login button states based on authentication status.
   */
  async updateLoginControls() {
    const isAuthenticated = await this.isAuthenticated();
    this.authUI.updateUIState(isAuthenticated, this.userFull);

    if (isAuthenticated) {
      // Start inactivity monitoring when authenticated
      this.inactivityManager.start();

      const { default: userController } = await import('../user/userController.js');
      await userController.init();
    } else {
      // Stop inactivity monitoring when not authenticated
      this.inactivityManager.stop();
    }

  }

  /**
   * Returns the Authentication state.
   * @returns {Promise<boolean>} - Is Authenticated
   */
  async isAuthenticated() {
    try {
      const isValid = await this.tokenManager.validateToken();
      
      if (isValid) {
        // Update user info from token manager
        this.userInfo = this.tokenManager.getUserInfo();
        if (this.userInfo) {
          this.user = this.userInfo.firstName + ' ' + this.userInfo.lastName[0] + '.';
          this.userFull = this.userInfo.firstName + ' ' + this.userInfo.lastName;
          this.userFirstName = this.userInfo.firstName;
          this.userLastName = this.userInfo.lastName;
        }
        return true;
      }
      
      return false;
    } catch (error) {
      return false;
    }
  }

  /**
   * Handle successful login
   * @param {Object} authData - Authentication data from server
   */
  async handleLoginSuccess(authData) {
    // Store auth data
    this.tokenManager.storeTokens(authData);

    // Update local user data
    this.accessToken = authData.accessToken;
    this.userInfo = authData.user;
    this.user = authData.user.firstName + ' ' + authData.user.lastName[0] + '.';
    this.userFull = this.userInfo.firstName + ' ' + this.userInfo.lastName;
    this.userFirstName = authData.user.firstName;
    this.userLastName = authData.user.lastName;

    // Update UI
    await this.updateLoginControls();

    setTimeout(() => {
      window.location.href = `${this.baseUrl}#/dashboard`;
    }, 1000);
  }
}

const auth = new Authenticate();
export default auth;